// src/pages/DashboardTab.tsx
import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  Stack,
  Avatar,
  Paper,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { useTree } from "../../hooks/useTree";

export default function DashboardTab() {
  const { metrics, analytics, loading } = useTree();

  if (loading || !metrics || !analytics) {
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
              gap: 2,
            }}
          >
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="rounded" height={120} />
            ))}
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
              gap: 3,
            }}
          >
            <Skeleton variant="rounded" height={300} />
            <Skeleton variant="rounded" height={300} />
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: "#1a1d1e",
        minHeight: "100vh",
      }}
    >
      {/* Stats Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2.5,
          mb: 4,
        }}
      >
        <StatCard
          icon={<PeopleIcon />}
          label="Members"
          value={metrics.staff_cnt}
          trend="+12%"
          trendUp={true}
          color="#6366f1"
          bgColor="#f0f0ff"
        />
        <StatCard
          icon={<TrendingUpIcon />}
          label="Active Players"
          value={metrics.player_cnt}
          trend="+8%"
          trendUp={true}
          color="#10b981"
          bgColor="#f0fdf4"
        />
        <StatCard
          icon={<AccountBalanceIcon />}
          label="Total Balance"
          value={`₹${Number(metrics.bank).toLocaleString()}`}
          trend="+24%"
          trendUp={true}
          color="#f59e0b"
          bgColor="#fffbeb"
        />
      </Box>

      {/* Charts Section */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 3,
        }}
      >
        <ChartCard
          title="Members Growth"
          subtitle="Last 7 days performance"
          data={analytics.staffTrend}
          color="#6366f1"
          type="area"
        />
        <ChartCard
          title="Player Activity"
          subtitle="Daily active players"
          data={analytics.playerTrend}
          color="#10b981"
          type="bar"
        />
      </Box>
    </Box>
  );
}

/* ═════════════════════ Components ═════════════════════ */

function StatCard({
  icon,
  label,
  value,
  trend,
  trendUp,
  color,
  bgColor,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  trend: string;
  trendUp: boolean;
  color: string;
  bgColor: string;
}) {
  return (
    <Card
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid #2f3536",
        background: "#232626",
        transition: "all 0.3s ease",
        cursor: "pointer",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          border: `1px solid ${color}20`,
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Avatar
          sx={{
            width: 60,
            height: 60,
            backgroundColor: "transparent",
            color: color,
            border: "none",
          }}
        >
          {icon}
        </Avatar>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 1.5,
            py: 0.75,
            minHeight: 36,
            borderRadius: "999px",
            backgroundColor: trendUp ? "#63ea86" : "rgba(239, 68, 68, 0.10)",
            border: trendUp
              ? "1px solid rgba(99, 234, 134, 0.36)"
              : "1px solid rgba(239, 68, 68, 0.18)",
            boxShadow: trendUp
              ? "0 0 18px rgba(99, 234, 134, 0.18)"
              : "inset 0 1px 0 rgba(255,255,255,0.03)",
          }}
        >
          <Typography
            component="span"
            sx={{
              color: trendUp ? "#163320" : "#f10c0cff",
              fontWeight: 800,
              fontSize: 12,
              lineHeight: 1,
              letterSpacing: "0.01em",
            }}
          >
            {trend}
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: "#e2e8f0",
          mb: 0.5,
          fontSize: { xs: "1.75rem", sm: "2rem" },
        }}
      >
        {value}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          color: "#94a3b8",
          fontWeight: 500,
          fontSize: 14,
        }}
      >
        {label}
      </Typography>
    </Card>
  );
}

function ChartCard({
  title,
  subtitle,
  data,
  color,
  type = "bar",
}: {
  title: string;
  subtitle: string;
  data: { day: string; count: number }[];
  color: string;
  type?: "bar" | "area";
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: "1px solid #2f3536",
        background: "#232626",
        height: "fit-content",
      }}
    >
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            color: "#e2e8f0",
            fontSize: 18,
            mb: 0.5,
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "#94a3b8",
            fontSize: 14,
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      <Box sx={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          {type === "area" ? (
            <AreaChart data={data}>
              <defs>
                <linearGradient
                  id={`gradient-${color}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={color} stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="day"
                stroke="#e2e8f0"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#e2e8f0"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#232626",
                  border: "1px solid #2f3536",
                  borderRadius: 12,
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  color: "#e2e8f0",
                }}
                labelStyle={{ color: "#94a3b8", fontWeight: 500 }}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke={color}
                strokeWidth={3}
                fill={`url(#gradient-${color})`}
              />
            </AreaChart>
          ) : (
            <ReBarChart data={data}>
              <XAxis
                dataKey="day"
                stroke="#e2e8f0"
                fontSize={12}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="#e2e8f0"
                fontSize={12}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#232626",
                  border: "1px solid #2f3536",
                  borderRadius: 12,
                  boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
                  color: "#e2e8f0",
                }}
                labelStyle={{ color: "#94a3b8", fontWeight: 500 }}
              />
              <Bar dataKey="count" fill={color} radius={[6, 6, 0, 0]} />
            </ReBarChart>
          )}
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}
